import React from 'react';
import { useForm, Controller } from 'react-hook-form';

interface ToolExecutionForm {
  [key: string]: any;
}

interface DynamicToolFormProps {
  tool: any;
  onExecute: (data: any) => void;
  onCancel: () => void;
  executionResult?: any;
}

export const DynamicToolForm: React.FC<DynamicToolFormProps> = ({ tool, onExecute, onCancel, executionResult }) => {
  const { register, handleSubmit, control, formState: { errors } } = useForm<ToolExecutionForm>();

  const onSubmit = (data: ToolExecutionForm) => {
    onExecute(data);
  };

  const getInputType = (param: any) => {
    switch (param.type) {
      case 'number':
        return 'number';
      case 'boolean':
        return 'checkbox';
      case 'string':
      default:
        return 'text';
    }
  };

  const getDefaultValue = (param: any) => {
    switch (param.type) {
      case 'number':
        return 0;
      case 'boolean':
        return false;
      case 'string':
      default:
        return '';
    }
  };

  return (
    <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-600">
      <div className="mb-6">
        <h5 className="text-lg font-semibold text-white mb-2">Execute: {tool.name}</h5>
        <p className="text-sm text-gray-400">{tool.description}</p>
      </div>
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {tool.parameters && tool.parameters.map((param: any, index: number) => (
          <div key={index}>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {param.name} ({param.type})
              {param.required && <span className="text-red-400 ml-1">*</span>}
            </label>
            
            {param.type === 'boolean' ? (
              <Controller
                name={param.name}
                control={control}
                defaultValue={getDefaultValue(param)}
                render={({ field }) => (
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      {...field}
                      className="w-5 h-5 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-400">Enable {param.name}</span>
                  </div>
                )}
              />
            ) : (
              <input
                type={getInputType(param)}
                {...register(param.name, { 
                  required: param.required ? `${param.name} is required` : false,
                  valueAsNumber: param.type === 'number'
                })}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={`Enter ${param.name}`}
              />
            )}
            
            {errors[param.name] && (
              <p className="text-sm text-red-400 mt-2">
                {String(errors[param.name]?.message || 'Invalid input')}
              </p>
            )}
          </div>
        ))}
        
        <div className="flex items-center gap-3 pt-6 border-t border-gray-700">
          <button
            type="submit"
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            Execute Tool
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
      
      {/* Execution Result */}
      {executionResult && (
        <div className="mt-6 pt-6 border-t border-gray-700">
          <div className="flex items-center gap-2 mb-4">
            <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <h6 className="text-sm font-semibold text-green-300">Execution Result</h6>
          </div>
          
          <div className="bg-gray-900 rounded-lg p-4 max-h-96 overflow-y-auto">
            {executionResult.result ? (
              <div className="space-y-4">
                <div>
                  <h6 className="text-sm font-medium text-gray-300 mb-2">Tool Result:</h6>
                  <div className="bg-gray-800 rounded-lg p-3">
                    {Array.isArray(executionResult.result) ? (
                      executionResult.result.map((item: any, index: number) => (
                        <div key={index} className="mb-3 last:mb-0">
                          {item.type === 'text' ? (
                            <div 
                              className="text-green-200 whitespace-pre-wrap"
                              dangerouslySetInnerHTML={{ __html: item.text }}
                            />
                          ) : (
                            <pre className="text-xs text-green-200 whitespace-pre-wrap">
                              {JSON.stringify(item, null, 2)}
                            </pre>
                          )}
                        </div>
                      ))
                    ) : typeof executionResult.result === 'string' ? (
                      <div className="text-green-200 whitespace-pre-wrap">
                        {executionResult.result}
                      </div>
                    ) : (
                      <pre className="text-xs text-green-200 whitespace-pre-wrap">
                        {JSON.stringify(executionResult.result, null, 2)}
                      </pre>
                    )}
                  </div>
                </div>
                {executionResult.structuredContent && (
                  <div>
                    <h6 className="text-sm font-medium text-gray-300 mb-2">Structured Content:</h6>
                    <pre className="text-xs text-blue-200 whitespace-pre-wrap overflow-x-auto bg-gray-800 rounded-lg p-3 max-h-32 overflow-y-auto">
                      {JSON.stringify(executionResult.structuredContent, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            ) : (
              <pre className="text-xs text-green-200 whitespace-pre-wrap overflow-x-auto">
                {JSON.stringify(executionResult, null, 2)}
              </pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
